import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Save, TrendingUp, Clock, User, BookMarked, Download, FileText, ChevronDown, ChevronUp } from 'lucide-react';

const COLOURS   = ['D','E','F','G','H','I','J','K','L','M','N','O','P'];
const CLARITIES = ['VVS','VS','SI','I'];

export default function DiamondIndex() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [indexValue, setIndexValue] = useState('');
  const [currentIndex, setCurrentIndex] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [lastUpdatedBy, setLastUpdatedBy] = useState('');
  const [saving, setSaving] = useState(false);

  // Price preview
  const [previewShape, setPreviewShape] = useState('Round');
  const [previewSize, setPreviewSize]   = useState('0-6');
  const [previewPrices, setPreviewPrices] = useState({});
  const [sizes, setSizes]   = useState([]);
  const [shapes, setShapes] = useState([]);

  // Save to History
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [historyNote, setHistoryNote]             = useState('');
  const [savingHistory, setSavingHistory]         = useState(false);
  const [history, setHistory]                     = useState([]);
  const [historyExpanded, setHistoryExpanded]     = useState(false);

  const isAdmin = currentUser?.role === 'admin';
  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchIndex();
    fetchShapes();
    fetchHistory();
  }, []);

  useEffect(() => {
    if (previewShape) fetchSizes(previewShape);
  }, [previewShape]);

  useEffect(() => {
    if (currentIndex && previewShape && previewSize) fetchPreview();
  }, [currentIndex, previewShape, previewSize]);

  const fetchIndex = async () => {
    try {
      const res = await api.get('/diamond-index');
      setCurrentIndex(res.data.index_value);
      setIndexValue(res.data.index_value ? String(res.data.index_value) : '');
      setLastUpdated(res.data.updated_at);
      setLastUpdatedBy(res.data.updated_by_name || '');
    } catch { toast.error('Failed to load Diamond Index'); }
  };

  const fetchShapes = async () => {
    try {
      const sRes = await api.get('/catalog/diamond-shapes');
      const shapeNames = sRes.data.map(s => s.name);
      setShapes(shapeNames);
      if (shapeNames[0]) setPreviewShape(shapeNames[0]);
    } catch { /* silent */ }
  };

  const fetchSizes = async (shape) => {
    try {
      const res = await api.get('/catalog/diamond-sizes', { params: { shape } });
      const sizeNames = res.data.map(s => s.name);
      setSizes(sizeNames);
      // If the currently selected size isn't valid for the new shape,
      // fall back to the first available size so we never render a
      // stale/empty grid.
      if (!sizeNames.includes(previewSize) && sizeNames[0]) {
        setPreviewSize(sizeNames[0]);
      }
    } catch { /* silent */ }
  };

  const fetchPreview = async () => {
    try {
      const res = await api.get('/diamond-computed-prices', {
        params: { shape: previewShape, size: previewSize },
      });
      setPreviewPrices(res.data.prices || {});
    } catch {}
  };

  const fetchHistory = async () => {
    try {
      const res = await api.get('/diamond-price-history');
      setHistory(res.data);
    } catch {}
  };

  const handleSave = async () => {
    const val = parseFloat(indexValue);
    if (!val || val <= 0) { toast.error('Please enter a valid positive number'); return; }
    setSaving(true);
    try {
      await api.post('/diamond-index', { index_value: val });
      toast.success('Diamond Index updated');
      setCurrentIndex(val);
      await fetchIndex();
      await fetchPreview();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleSaveToHistory = async () => {
    setSavingHistory(true);
    try {
      const res = await api.post('/diamond-price-history', {
        source: 'index',
        note: historyNote.trim(),
      });
      toast.success(`Snapshot saved — ${res.data.total_prices} prices archived${res.data.pdf_url ? ' · PDF generated' : ''}`);
      setShowHistoryDialog(false);
      setHistoryNote('');
      setHistoryExpanded(true);
      await fetchHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save history');
    } finally { setSavingHistory(false); }
  };

  const fmt = v => v != null ? `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '—';

  const fmtDate = str => {
    if (!str) return '';
    const d = new Date(str.endsWith('Z') ? str : str + 'Z');
    return d.toLocaleString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' });
  };

  const sourceLabel = s => s === 'proportions' ? 'Proportion change' : 'Index change';
  const sourceDot   = s => s === 'proportions' ? 'bg-violet-400' : 'bg-emerald-500';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#075E54] text-white p-3 sticky top-0 z-10 shadow-md">
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white hover:bg-[#128C7E]">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-base sm:text-xl font-semibold">Diamond Index</h1>
            <p className="text-xs text-emerald-200">Base multiplier for all diamond prices</p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-4 space-y-5">

        {/* Current Index Card */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-[#075E54] to-[#128C7E] p-4 text-white">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="h-5 w-5 text-emerald-200" />
              <span className="text-sm font-medium text-emerald-100">Current Diamond Index</span>
            </div>
            <div className="text-4xl font-bold tracking-tight">
              {currentIndex
                ? `₹${Number(currentIndex).toLocaleString('en-IN')}`
                : <span className="text-xl text-emerald-300">Not set yet</span>}
            </div>
            {lastUpdated && (
              <div className="mt-2 flex items-center gap-3 text-xs text-emerald-200">
                {lastUpdatedBy && <span className="flex items-center gap-1"><User className="h-3 w-3" />{lastUpdatedBy}</span>}
                <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{fmtDate(lastUpdated)}</span>
              </div>
            )}
          </div>

          <div className="px-4 py-3 bg-amber-50 border-b border-amber-100">
            <p className="text-xs text-amber-700 font-medium">
              Pricing Formula: &nbsp;
              <span className="font-mono bg-amber-100 px-2 py-0.5 rounded">Diamond Price = Diamond Index × (Proportion % ÷ 100)</span>
            </p>
            <p className="text-xs text-amber-600 mt-0.5">Update the index once — all computed prices update instantly.</p>
          </div>

          {isAdmin && (
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Set New Index Value (₹)</label>
                <div className="flex gap-2 max-w-sm">
                  <Input
                    data-testid="diamond-index-input"
                    type="number" min="1" step="any" placeholder="e.g. 78000"
                    value={indexValue}
                    onChange={e => setIndexValue(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSave()}
                    className="font-mono text-base"
                  />
                  <Button
                    data-testid="diamond-index-save-btn"
                    onClick={handleSave} disabled={saving}
                    className="bg-[#25D366] hover:bg-[#20BA5A] text-white flex-shrink-0"
                  >
                    <Save className="h-4 w-4 mr-1.5" />
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>

              {/* Save to History button */}
              {currentIndex && (
                <div className="pt-1">
                  <Button
                    data-testid="save-to-history-btn"
                    variant="outline"
                    onClick={() => setShowHistoryDialog(true)}
                    className="border-[#075E54] text-[#075E54] hover:bg-[#075E54]/5 gap-2"
                  >
                    <BookMarked className="h-4 w-4" />
                    Save to History
                  </Button>
                  <p className="text-xs text-gray-400 mt-1">
                    Creates a permanent snapshot with all 832 computed prices + downloadable PDF
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Price Preview */}
        {currentIndex && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b bg-gray-50 flex flex-wrap items-center gap-3">
              <h2 className="text-sm font-semibold text-gray-800 flex-1">Computed Price Preview</h2>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1">
                  <label className="text-xs text-gray-500 font-medium">Shape</label>
                  <select
                    data-testid="preview-shape-select"
                    value={previewShape} onChange={e => setPreviewShape(e.target.value)}
                    className="text-xs border border-gray-200 rounded px-2 py-1 bg-white"
                  >
                    {shapes.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-1">
                  <label className="text-xs text-gray-500 font-medium">Size</label>
                  <select
                    data-testid="preview-size-select"
                    value={previewSize} onChange={e => setPreviewSize(e.target.value)}
                    className="text-xs border border-gray-200 rounded px-2 py-1 bg-white"
                  >
                    {sizes.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-[#075E54]/5 border-b">
                    <th className="text-left px-3 py-2 font-semibold text-gray-600 w-12">Colour</th>
                    {CLARITIES.map(c => (
                      <th key={c} className="text-center px-3 py-2 font-semibold text-gray-600">{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {COLOURS.map((colour, i) => (
                    <tr key={colour} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                      <td className="px-3 py-2 font-bold text-[#075E54]">{colour}</td>
                      {CLARITIES.map(clarity => (
                        <td key={clarity} className="text-center px-3 py-2 font-mono text-gray-700">
                          {previewPrices[`${colour}-${clarity}`] != null
                            ? fmt(previewPrices[`${colour}-${clarity}`])
                            : <span className="text-gray-300">—</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-2 bg-gray-50 border-t">
              <p className="text-xs text-gray-400">
                ₹{Number(currentIndex).toLocaleString('en-IN')} × proportion ÷ 100 · {previewShape} · Size {previewSize}
              </p>
            </div>
          </div>
        )}

        {/* Price Proportions link */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-blue-800">Price Proportions</p>
            <p className="text-xs text-blue-600 mt-0.5">View and edit the % proportion values for each combination</p>
          </div>
          <Button
            data-testid="goto-proportions-btn"
            variant="outline"
            onClick={() => navigate('/diamond-proportions')}
            className="border-blue-300 text-blue-700 hover:bg-blue-100 flex-shrink-0 ml-4"
          >
            Open Proportions
          </Button>
        </div>

        {/* Price History */}
        {history.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <button
              data-testid="toggle-history-btn"
              className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              onClick={() => setHistoryExpanded(v => !v)}
            >
              <div className="flex items-center gap-2">
                <BookMarked className="h-4 w-4 text-[#075E54]" />
                <span className="text-sm font-semibold text-gray-800">Price History</span>
                <span className="text-xs bg-[#075E54]/10 text-[#075E54] font-medium px-2 py-0.5 rounded-full">
                  {history.length} snapshot{history.length !== 1 ? 's' : ''}
                </span>
              </div>
              {historyExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
            </button>

            {historyExpanded && (
              <div className="border-t border-gray-100">
                {history.map((item, idx) => (
                  <div
                    key={item.id}
                    data-testid={`history-item-${idx}`}
                    className={`flex items-start justify-between gap-3 p-4 ${idx < history.length - 1 ? 'border-b border-gray-100' : ''} hover:bg-gray-50/50`}
                  >
                    <div className="flex items-start gap-3 min-w-0">
                      <div className={`mt-1.5 h-2.5 w-2.5 rounded-full flex-shrink-0 ${sourceDot(item.source)}`} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-bold text-[#075E54]">
                            {item.index_value ? `₹${Number(item.index_value).toLocaleString('en-IN')}` : '—'}
                          </span>
                          {item.note && (
                            <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full truncate max-w-[160px]">
                              {item.note}
                            </span>
                          )}
                          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{sourceLabel(item.source)}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
                          <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{fmtDate(item.saved_at)}</span>
                          <span className="flex items-center gap-1"><User className="h-3 w-3" />{item.saved_by}</span>
                          <span className="text-gray-400">{item.total_prices} prices</span>
                        </p>
                      </div>
                    </div>
                    {item.pdf_url && (
                      <a
                        data-testid={`download-pdf-${idx}`}
                        href={`${API_BASE}${item.pdf_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        download={`DiaForge_PriceBook_${item.saved_at?.substring(0, 10)}.pdf`}
                        className="flex-shrink-0 flex items-center gap-1.5 text-xs text-[#075E54] border border-[#075E54]/30 hover:bg-[#075E54]/5 px-2.5 py-1.5 rounded-lg transition-colors font-medium"
                      >
                        <FileText className="h-3.5 w-3.5" />
                        PDF
                        <Download className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Save to History Dialog */}
      {showHistoryDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-gradient-to-r from-[#075E54] to-[#128C7E] p-5 text-white">
              <div className="flex items-center gap-2 mb-1">
                <BookMarked className="h-5 w-5 text-emerald-200" />
                <h2 className="text-base font-bold">Save to History</h2>
              </div>
              <p className="text-xs text-emerald-200">Creates a permanent, immutable snapshot</p>
            </div>

            <div className="p-5 space-y-4">
              <div className="bg-gray-50 rounded-lg p-3 space-y-1.5">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Index Value</span>
                  <span className="font-bold text-[#075E54]">₹{Number(currentIndex).toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Combinations</span>
                  <span className="font-medium text-gray-700">832 prices (2 shapes × 8 sizes × 13 colours × 4 clarities)</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">PDF</span>
                  <span className="font-medium text-gray-700">Auto-generated & stored in R2</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Label / Note <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <Input
                  data-testid="history-note-input"
                  placeholder="e.g. Diwali rate, Post-budget revision..."
                  value={historyNote}
                  onChange={e => setHistoryNote(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSaveToHistory()}
                  autoFocus
                />
                <p className="text-xs text-gray-400 mt-1">This label helps identify the snapshot in history</p>
              </div>
            </div>

            <div className="px-5 pb-5 flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => { setShowHistoryDialog(false); setHistoryNote(''); }}
                disabled={savingHistory}
              >
                Cancel
              </Button>
              <Button
                data-testid="confirm-save-history-btn"
                className="flex-1 bg-[#075E54] hover:bg-[#128C7E] text-white gap-2"
                onClick={handleSaveToHistory}
                disabled={savingHistory}
              >
                <BookMarked className="h-4 w-4" />
                {savingHistory ? 'Saving & generating PDF...' : 'Save to History'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
